import React, { useState } from 'react';
import { 
  Activity, 
  ShieldAlert, 
  FileText, 
  PlusCircle, 
  AlertTriangle, 
  Database,
  RefreshCw,
  Sliders
} from 'lucide-react';
import { Project } from '../types';

interface HeaderProps {
  currentProject?: Project | null;
  projects?: Project[];
  onSelectProject?: (id: string) => void;
  // Backward compatibility props
  currentCampaign?: Project | null;
  campaigns?: Project[];
  onSelectCampaign?: (id: string) => void;
  onOpenStepModal: () => void;
  onOpenDisturbanceModal: () => void;
  onOpenCorrelationModal: () => void;
  onOpenReportsModal: () => void;
  onOpenParametersModal?: () => void;
  onOpenProjectManager?: () => void;
  onOpenCampaignManager?: () => void;
  onRefreshData?: () => Promise<void> | void;
  activeTestCount: number;
}

export const Header: React.FC<HeaderProps> = ({
  currentProject,
  projects,
  onSelectProject,
  currentCampaign,
  campaigns,
  onSelectCampaign,
  onOpenStepModal,
  onOpenDisturbanceModal,
  onOpenCorrelationModal,
  onOpenReportsModal,
  onOpenParametersModal,
  onOpenProjectManager,
  onOpenCampaignManager,
  onRefreshData,
  activeTestCount,
}) => {
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const activeProject = currentProject || currentCampaign || null;
  const projectList = projects || campaigns || [];
  const handleSelect = onSelectProject || onSelectCampaign || (() => {});
  const handleOpenManager = onOpenProjectManager || onOpenCampaignManager || (() => {});

  const handleRefresh = async () => {
    if (!onRefreshData || isRefreshing) return;
    setIsRefreshing(true);
    try {
      await onRefreshData();
    } finally {
      setTimeout(() => setIsRefreshing(false), 500);
    }
  };

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur px-3 sm:px-6 py-2.5 shadow-sm no-print">
      <div className="w-full mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-2.5">
        {/* Brand & Project Row */}
        <div className="flex items-center justify-between sm:justify-start gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-sky-600 flex items-center justify-center text-white shadow-sm shrink-0">
              <Activity className="w-4 h-4 text-white" />
            </div>
            <div>
              <span className="font-bold tracking-tight text-slate-900 text-sm sm:text-base">
                APC Step Test Tracker
              </span>
            </div>
          </div>

          <div className="h-5 w-px bg-slate-200 hidden sm:block"></div>

          {/* Project Selector */}
          <div className="flex items-center gap-1.5">
            <select
              value={activeProject?.id || ''}
              onChange={(e) => handleSelect(e.target.value)}
              title="Select Active Project"
              className="text-xs font-medium bg-slate-50 text-slate-700 border border-slate-300 rounded-md px-2.5 py-1.5 focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 max-w-[200px] truncate"
            >
              {projectList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>

            {/* Active tests indicator badge */}
            {activeTestCount > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                <span>{activeTestCount} Active</span>
              </span>
            )}
          </div>
        </div>

        {/* Action Buttons Row */}
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-between sm:justify-end">
          {/* Primary Action Button */}
          <button
            onClick={onOpenStepModal}
            title="Log a new step test"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium shadow-sm transition-all bg-sky-600 hover:bg-sky-700 text-white active:scale-95"
          >
            <PlusCircle className="w-3.5 h-3.5" />
            <span>+ Step Test</span>
          </button>

          <button
            onClick={onOpenDisturbanceModal}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-sm transition-colors"
            title="Log plant disturbance"
          >
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" />
            <span className="hidden xs:inline sm:inline">Disturbance</span>
          </button>

          <button
            onClick={onOpenCorrelationModal}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-sm transition-colors"
            title="View correlation rules"
          >
            <ShieldAlert className="w-3.5 h-3.5 text-indigo-500" />
            <span className="hidden sm:inline">Correlation</span>
          </button>

          <button
            onClick={onOpenReportsModal}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-sm transition-colors"
            title="View reports and export"
          >
            <FileText className="w-3.5 h-3.5 text-emerald-600" />
            <span>Reports</span>
          </button>

          {onOpenParametersModal && (
            <button
              onClick={onOpenParametersModal}
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-md text-xs font-medium bg-white hover:bg-slate-50 text-slate-700 border border-slate-300 shadow-sm transition-colors cursor-pointer"
              title="Configure step test parameters (target moves per MV & hold time)"
            >
              <Sliders className="w-3.5 h-3.5 text-sky-600" />
              <span className="hidden sm:inline">Parameters</span>
            </button>
          )}

          <div className="h-4 w-px bg-slate-200 hidden sm:block"></div>

          {/* Project & Database Manager */}
          <button
            onClick={handleOpenManager}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-white hover:bg-slate-50 text-purple-700 border border-slate-300 shadow-sm transition-colors text-xs font-medium cursor-pointer"
            title="Manage projects & database backup"
          >
            <Database className="w-3.5 h-3.5 text-purple-600" />
            <span className="hidden sm:inline">Projects</span>
          </button>

          {/* Sync / Refresh Button */}
          {onRefreshData && (
            <button
              onClick={handleRefresh}
              className={`p-1.5 rounded-md bg-white hover:bg-slate-50 text-slate-600 hover:text-sky-600 border border-slate-300 shadow-sm transition-colors cursor-pointer ${
                isRefreshing ? 'opacity-70 pointer-events-none' : ''
              }`}
              title="Sync & refresh data from database"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin text-sky-600' : ''}`} />
            </button>
          )}
        </div>
      </div>
    </header>
  );
};
